// French Unit Tests, Exercises, and Resources
// Vanuatu National Syllabus: French as a Foreign Language - Years 7-10
// Last Updated: January 31, 2026

export const frenchAssessment = {
  subject: "French as a Foreign Language",
  level: "Junior Secondary",
  assessmentTypes: ["Unit Tests", "Oral Assessment", "Written Assessment", "Listening Comprehension"]
};

// ============================================
// YEAR 7 FRENCH UNIT TESTS
// ============================================
export const year7FrenchUnitTests = {
  term1: {
    title: "Term 1 - Grammar Foundations",
    tests: [
      {
        title: "Articles & Nouns Test",
        duration: "30 minutes",
        totalMarks: 30,
        strand: "Grammar",
        topics: ["Definite articles", "Indefinite articles", "Noun gender"],
        sections: [
          { name: "Section A: Fill in the blanks", marks: 10, questions: 10 },
          { name: "Section B: Gender identification", marks: 10, questions: 10 },
          { name: "Section C: Translation", marks: 10, questions: 5 }
        ]
      },
      {
        title: "Adjectives Quiz",
        duration: "25 minutes",
        totalMarks: 25,
        strand: "Grammar",
        topics: ["Adjective agreement", "Possessive adjectives", "Position"],
        sections: [
          { name: "Section A: Agreement", marks: 10, questions: 10 },
          { name: "Section B: Possessives", marks: 10, questions: 10 },
          { name: "Section C: Sentence building", marks: 5, questions: 5 }
        ]
      }
    ]
  },
  term2: {
    title: "Term 2 - Conjugation",
    tests: [
      {
        title: "Present Tense - Regular Verbs",
        duration: "35 minutes",
        totalMarks: 35,
        strand: "Conjugation",
        topics: ["-ER verbs", "-IR verbs", "-RE verbs"],
        sections: [
          { name: "Section A: -ER verb conjugation", marks: 12, questions: 6 },
          { name: "Section B: -IR verb conjugation", marks: 12, questions: 6 },
          { name: "Section C: -RE verb conjugation", marks: 11, questions: 6 }
        ]
      },
      {
        title: "Irregular Verbs Test",
        duration: "30 minutes",
        totalMarks: 30,
        strand: "Conjugation",
        topics: ["Être", "Avoir", "Aller", "Faire"],
        sections: [
          { name: "Section A: Être & Avoir", marks: 15, questions: 10 },
          { name: "Section B: Aller & Faire", marks: 15, questions: 10 }
        ]
      }
    ]
  },
  term3: {
    title: "Term 3 - Vocabulary & Communication",
    tests: [
      {
        title: "Vocabulary Test",
        duration: "30 minutes",
        totalMarks: 30,
        strand: "Vocabulary",
        topics: ["Greetings", "Numbers", "Family", "School"],
        sections: [
          { name: "Section A: Greetings & Introductions", marks: 8, questions: 8 },
          { name: "Section B: Numbers 1-30", marks: 7, questions: 7 },
          { name: "Section C: Family vocabulary", marks: 8, questions: 8 },
          { name: "Section D: School vocabulary", marks: 7, questions: 7 }
        ]
      },
      {
        title: "Oral Assessment",
        duration: "5-10 minutes per student",
        totalMarks: 20,
        strand: "Communication",
        topics: ["Self-introduction", "Basic conversation"],
        sections: [
          { name: "Pronunciation", marks: 5 },
          { name: "Content & Vocabulary", marks: 8 },
          { name: "Fluency", marks: 4 },
          { name: "Comprehension", marks: 3 }
        ]
      }
    ]
  }
};

// ============================================
// YEAR 8 FRENCH UNIT TESTS
// ============================================
export const year8FrenchUnitTests = {
  term1: {
    title: "Term 1 - Advanced Grammar",
    tests: [
      {
        title: "Partitive Articles Test",
        duration: "25 minutes",
        totalMarks: 25,
        strand: "Grammar",
        topics: ["du, de la, de l', des", "Negation with de"],
        sections: [
          { name: "Section A: Fill in partitives", marks: 12, questions: 12 },
          { name: "Section B: Negation transformation", marks: 8, questions: 8 },
          { name: "Section C: Translation", marks: 5, questions: 5 }
        ]
      },
      {
        title: "Object Pronouns Quiz",
        duration: "30 minutes",
        totalMarks: 30,
        strand: "Grammar",
        topics: ["Direct object pronouns", "Indirect object pronouns"],
        sections: [
          { name: "Section A: Direct objects", marks: 15, questions: 10 },
          { name: "Section B: Indirect objects", marks: 15, questions: 10 }
        ]
      }
    ]
  },
  term2: {
    title: "Term 2 - Past Tense",
    tests: [
      {
        title: "Passé Composé with Avoir",
        duration: "35 minutes",
        totalMarks: 35,
        strand: "Conjugation",
        topics: ["Past participles", "Avoir auxiliary", "Irregular participles"],
        sections: [
          { name: "Section A: Regular past participles", marks: 12, questions: 12 },
          { name: "Section B: Irregular past participles", marks: 12, questions: 12 },
          { name: "Section C: Sentence formation", marks: 11, questions: 6 }
        ]
      },
      {
        title: "Passé Composé with Être",
        duration: "30 minutes",
        totalMarks: 30,
        strand: "Conjugation",
        topics: ["DR MRS VANDERTRAMP verbs", "Agreement rules"],
        sections: [
          { name: "Section A: Identify être verbs", marks: 10, questions: 10 },
          { name: "Section B: Agreement practice", marks: 12, questions: 12 },
          { name: "Section C: Mixed sentences", marks: 8, questions: 8 }
        ]
      }
    ]
  },
  term3: {
    title: "Term 3 - Communication",
    tests: [
      {
        title: "Shopping Dialogue Assessment",
        duration: "20 minutes",
        totalMarks: 20,
        strand: "Vocabulary",
        topics: ["Clothing", "Prices", "Shopping phrases"],
        sections: [
          { name: "Section A: Vocabulary matching", marks: 10, questions: 10 },
          { name: "Section B: Dialogue completion", marks: 10, questions: 5 }
        ]
      }
    ]
  }
};

// ============================================
// YEAR 9 FRENCH UNIT TESTS
// ============================================
export const year9FrenchUnitTests = {
  term1: {
    title: "Term 1 - Complex Grammar",
    tests: [
      {
        title: "Comparatives & Superlatives Test",
        duration: "30 minutes",
        totalMarks: 30,
        strand: "Grammar",
        topics: ["Comparatives", "Superlatives", "Irregular forms"],
        sections: [
          { name: "Section A: Comparative sentences", marks: 12, questions: 6 },
          { name: "Section B: Superlative sentences", marks: 12, questions: 6 },
          { name: "Section C: Irregular forms", marks: 6, questions: 6 }
        ]
      }
    ]
  },
  term2: {
    title: "Term 2 - Advanced Tenses",
    tests: [
      {
        title: "Imperfect Tense Test",
        duration: "35 minutes",
        totalMarks: 35,
        strand: "Conjugation",
        topics: ["Imperfect formation", "Usage", "Passé composé vs Imparfait"],
        sections: [
          { name: "Section A: Conjugation", marks: 15, questions: 10 },
          { name: "Section B: Usage identification", marks: 10, questions: 10 },
          { name: "Section C: Tense selection", marks: 10, questions: 5 }
        ]
      },
      {
        title: "Future Tense Test",
        duration: "30 minutes",
        totalMarks: 30,
        strand: "Conjugation",
        topics: ["Future simple formation", "Irregular stems"],
        sections: [
          { name: "Section A: Regular futures", marks: 15, questions: 10 },
          { name: "Section B: Irregular futures", marks: 15, questions: 10 }
        ]
      }
    ]
  },
  term3: {
    title: "Term 3 - Communication",
    tests: [
      {
        title: "Opinion Expression Assessment",
        duration: "25 minutes",
        totalMarks: 25,
        strand: "Communication",
        topics: ["Giving opinions", "Agreement/Disagreement", "Reasons"],
        sections: [
          { name: "Section A: Opinion phrases", marks: 10, questions: 10 },
          { name: "Section B: Dialogue responses", marks: 10, questions: 5 },
          { name: "Section C: Written opinion", marks: 5, questions: 1 }
        ]
      }
    ]
  }
};

// ============================================
// YEAR 10 FRENCH UNIT TESTS
// ============================================
export const year10FrenchUnitTests = {
  term1: {
    title: "Term 1 - Grammar Revision",
    tests: [
      {
        title: "Comprehensive Grammar Test",
        duration: "45 minutes",
        totalMarks: 50,
        strand: "Grammar",
        topics: ["All articles", "Pronouns", "Adjectives", "Prepositions"],
        sections: [
          { name: "Section A: Articles", marks: 12, questions: 12 },
          { name: "Section B: Pronouns", marks: 13, questions: 13 },
          { name: "Section C: Adjectives", marks: 13, questions: 13 },
          { name: "Section D: Prepositions", marks: 12, questions: 12 }
        ]
      }
    ]
  },
  term2: {
    title: "Term 2 - All Tenses",
    tests: [
      {
        title: "Comprehensive Conjugation Test",
        duration: "50 minutes",
        totalMarks: 50,
        strand: "Conjugation",
        topics: ["All tenses", "Mood identification", "Context usage"],
        sections: [
          { name: "Section A: Present tense", marks: 10, questions: 10 },
          { name: "Section B: Past tenses", marks: 15, questions: 10 },
          { name: "Section C: Future & Conditional", marks: 15, questions: 10 },
          { name: "Section D: Mixed tense passage", marks: 10, questions: 5 }
        ]
      }
    ]
  },
  term3: {
    title: "Term 3 - External Exam Preparation",
    tests: [
      {
        title: "Mock External Examination",
        duration: "90 minutes",
        totalMarks: 100,
        strand: "All",
        topics: ["Listening", "Reading", "Writing", "Grammar"],
        sections: [
          { name: "Part A: Listening Comprehension", marks: 25, questions: 15 },
          { name: "Part B: Reading Comprehension", marks: 25, questions: 15 },
          { name: "Part C: Grammar & Vocabulary", marks: 25, questions: 25 },
          { name: "Part D: Written Expression", marks: 25, questions: 2 }
        ]
      }
    ]
  }
};

// ============================================
// YEAR 7 FRENCH EXERCISES
// ============================================
export const year7FrenchExercises = {
  unit1: {
    title: "Articles Practice",
    exercises: [
      {
        title: "Definite Articles Worksheet",
        type: "fill-in-blank",
        instructions: "Complete with le, la, l', or les",
        questions: [
          { question: "___ livre (book)", answer: "le" },
          { question: "___ table (table)", answer: "la" },
          { question: "___ école (school)", answer: "l'" },
          { question: "___ enfants (children)", answer: "les" },
          { question: "___ maison (house)", answer: "la" }
        ]
      },
      {
        title: "Indefinite Articles Worksheet",
        type: "fill-in-blank",
        instructions: "Complete with un, une, or des",
        questions: [
          { question: "___ garçon (a boy)", answer: "un" },
          { question: "___ fille (a girl)", answer: "une" },
          { question: "___ livres (some books)", answer: "des" },
          { question: "___ ami (a friend, m.)", answer: "un" },
          { question: "___ amie (a friend, f.)", answer: "une" }
        ]
      }
    ]
  },
  unit2: {
    title: "Verb Conjugation Practice",
    exercises: [
      {
        title: "ÊTRE Practice",
        type: "conjugation",
        instructions: "Conjugate ÊTRE for each subject",
        questions: [
          { question: "Je ___", answer: "suis" },
          { question: "Tu ___", answer: "es" },
          { question: "Il/Elle ___", answer: "est" },
          { question: "Nous ___", answer: "sommes" },
          { question: "Vous ___", answer: "êtes" },
          { question: "Ils/Elles ___", answer: "sont" }
        ]
      },
      {
        title: "AVOIR Practice",
        type: "conjugation",
        instructions: "Conjugate AVOIR for each subject",
        questions: [
          { question: "J' ___", answer: "ai" },
          { question: "Tu ___", answer: "as" },
          { question: "Il/Elle ___", answer: "a" },
          { question: "Nous ___", answer: "avons" },
          { question: "Vous ___", answer: "avez" },
          { question: "Ils/Elles ___", answer: "ont" }
        ]
      }
    ]
  },
  unit3: {
    title: "Numbers & Vocabulary",
    exercises: [
      {
        title: "Numbers 1-20 Matching",
        type: "matching",
        instructions: "Match the number to its French spelling",
        questions: [
          { question: "5", answer: "cinq" },
          { question: "12", answer: "douze" },
          { question: "17", answer: "dix-sept" },
          { question: "20", answer: "vingt" },
          { question: "15", answer: "quinze" }
        ]
      }
    ]
  }
};

// ============================================
// YEAR 8 FRENCH EXERCISES
// ============================================
export const year8FrenchExercises = {
  unit1: {
    title: "Partitive Articles Practice",
    exercises: [
      {
        title: "Partitive Articles Worksheet",
        type: "fill-in-blank",
        instructions: "Complete with du, de la, de l', or des",
        questions: [
          { question: "Je mange ___ pain.", answer: "du" },
          { question: "Elle boit ___ eau.", answer: "de l'" },
          { question: "Nous voulons ___ salade.", answer: "de la" },
          { question: "Il achète ___ pommes.", answer: "des" },
          { question: "Tu prends ___ café?", answer: "du" }
        ]
      }
    ]
  },
  unit2: {
    title: "Passé Composé Practice",
    exercises: [
      {
        title: "Past Participle Formation",
        type: "transformation",
        instructions: "Give the past participle of each verb",
        questions: [
          { question: "manger →", answer: "mangé" },
          { question: "finir →", answer: "fini" },
          { question: "vendre →", answer: "vendu" },
          { question: "faire →", answer: "fait" },
          { question: "voir →", answer: "vu" }
        ]
      },
      {
        title: "Passé Composé Sentences",
        type: "conjugation",
        instructions: "Put the verb in passé composé",
        questions: [
          { question: "Je (manger) une pomme.", answer: "J'ai mangé" },
          { question: "Elle (aller) à Paris.", answer: "Elle est allée" },
          { question: "Nous (finir) le travail.", answer: "Nous avons fini" },
          { question: "Ils (partir) tôt.", answer: "Ils sont partis" }
        ]
      }
    ]
  }
};

// ============================================
// YEAR 9 FRENCH EXERCISES
// ============================================
export const year9FrenchExercises = {
  unit1: {
    title: "Comparatives & Superlatives",
    exercises: [
      {
        title: "Comparative Sentences",
        type: "translation",
        instructions: "Translate to French using comparatives",
        questions: [
          { question: "Marie is taller than Pierre.", answer: "Marie est plus grande que Pierre." },
          { question: "This book is less interesting.", answer: "Ce livre est moins intéressant." },
          { question: "I am as strong as you.", answer: "Je suis aussi fort(e) que toi." }
        ]
      }
    ]
  },
  unit2: {
    title: "Imperfect & Future Tenses",
    exercises: [
      {
        title: "Imperfect Conjugation",
        type: "conjugation",
        instructions: "Conjugate in the imperfect tense",
        questions: [
          { question: "Je (manger)", answer: "mangeais" },
          { question: "Nous (être)", answer: "étions" },
          { question: "Il (faire)", answer: "faisait" },
          { question: "Elles (avoir)", answer: "avaient" }
        ]
      },
      {
        title: "Future Simple Conjugation",
        type: "conjugation",
        instructions: "Conjugate in the future tense",
        questions: [
          { question: "Je (parler)", answer: "parlerai" },
          { question: "Tu (être)", answer: "seras" },
          { question: "Nous (aller)", answer: "irons" },
          { question: "Ils (faire)", answer: "feront" }
        ]
      }
    ]
  }
};

// ============================================
// YEAR 10 FRENCH EXERCISES
// ============================================
export const year10FrenchExercises = {
  unit1: {
    title: "Comprehensive Review",
    exercises: [
      {
        title: "Mixed Tense Practice",
        type: "fill-in-blank",
        instructions: "Complete with the correct tense",
        questions: [
          { question: "Hier, je (aller) au cinéma.", answer: "suis allé(e)" },
          { question: "Demain, nous (partir) en vacances.", answer: "partirons" },
          { question: "Quand j'étais petit, je (jouer) au foot.", answer: "jouais" },
          { question: "En ce moment, elle (travailler).", answer: "travaille" }
        ]
      }
    ]
  },
  unit2: {
    title: "Exam Preparation",
    exercises: [
      {
        title: "Reading Comprehension Practice",
        type: "comprehension",
        instructions: "Read the passage and answer questions in French",
        passage: "Marie habite à Paris. Elle a quinze ans et va au lycée. Le matin, elle prend le métro pour aller à l'école. Elle aime beaucoup les maths et l'anglais, mais elle n'aime pas l'histoire.",
        questions: [
          { question: "Où habite Marie?", answer: "Marie habite à Paris." },
          { question: "Quel âge a Marie?", answer: "Elle a quinze ans." },
          { question: "Comment va-t-elle à l'école?", answer: "Elle prend le métro." },
          { question: "Quelles matières aime-t-elle?", answer: "Elle aime les maths et l'anglais." }
        ]
      }
    ]
  }
};

export default {
  year7: { unitTests: year7FrenchUnitTests, exercises: year7FrenchExercises },
  year8: { unitTests: year8FrenchUnitTests, exercises: year8FrenchExercises },
  year9: { unitTests: year9FrenchUnitTests, exercises: year9FrenchExercises },
  year10: { unitTests: year10FrenchUnitTests, exercises: year10FrenchExercises }
};
